import React, { useEffect, useState, useCallback } from 'react';
import {
  FaPlus, FaBox, FaCheckCircle, FaClock, FaTimesCircle,
   FaClipboardList,
  FaTools, FaTrashAlt, FaWrench, FaEdit, FaTimes, FaSave,
  FaChartBar, FaChartPie, FaChartLine, FaFileAlt, FaExclamationTriangle
} from 'react-icons/fa';
import AssetuserSidebar from './AssetuserSidebar';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import * as assetUserService from '../services/assetUserService';
import complianceService from '../services/complianceService';
import { API_ENDPOINTS } from '../config/api';
import '../styles/AssetUserDashboard.css';

const COLORS = ['#a5d6ff', '#b3f5e8', '#fed7aa', '#fca5a5', '#d8b4fb', '#f5d2e8', '#a7e8ea', '#fbaaa4'];
const STATUS_COLORS = { available: '#6ee7b7', in_use: '#60a5fa', under_maintenance: '#fbbf24', disposed: '#f87171' };
const CONDITION_COLORS = { excellent: '#6ee7b7', good: '#60a5fa', fair: '#fbbf24', damaged: '#f87171' };


const DURATION_OPTIONS = [
  { value: '1_week', label: '1 Week' },
  { value: '2_weeks', label: '2 Weeks' },
  { value: '3_weeks', label: '3 Weeks' },
  { value: '1_month', label: '1 Month' },
  { value: '3_months', label: '3 Months' },
  { value: '4_months', label: '4 Months' },
  { value: '6_months', label: '6 Months' },
  { value: '1_year', label: '1 Year' }
];

const AssetUserDashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [activeTab, setActiveTab] = useState('overview');

  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Assets
  const [allAssets, setAllAssets] = useState([]);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [myAssets, setMyAssets] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // My Requests filter
  const [requestStatusFilter, setRequestStatusFilter] = useState('all');

  // Edit request state
  const [editingRequest, setEditingRequest] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Filters for All Assets
 
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCondition, setFilterCondition] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAssetCount, setTotalAssetCount] = useState(0);

  // Compliance data
  const [complianceData, setComplianceData] = useState({});

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const data = await assetUserService.getDashboardStats();
      setStats(data.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchComplianceData = useCallback(async () => {
    try {
      const response = await complianceService.getMyAssetsComplianceDues();
      const logs = Array.isArray(response.data) ? response.data : response.data?.logs || [];
      
      // Create a map of assetId to compliance due date
      const compMap = {};
      logs.forEach(log => {
        const assetId = log.assetId?._id || log.assetId;
        if (assetId && log.dueDate) {
          if (!compMap[assetId] || new Date(log.dueDate) < new Date(compMap[assetId])) {
            compMap[assetId] = log.dueDate;
          }
        }
      });
      setComplianceData(compMap);
    } catch (err) {
      console.error('Error fetching compliance data:', err);
    }
  }, []);

  const fetchAllAssets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page: currentPage, limit: 30 };
     
      if (filterCategory) params.category = filterCategory;
      if (filterStatus) params.status = filterStatus;
      if (filterCondition) params.condition = filterCondition;

      const qs = new URLSearchParams(params).toString();
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.ASSET_USER_ALL_ASSETS}?${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setAllAssets(data.assets || []);
      setTotalPages(data.totalPages || 1);
      setTotalAssetCount(data.total || 0);
    } catch (err) {
      setError('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterCategory, filterStatus, filterCondition]);


  const fetchAvailable = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await assetUserService.getAvailableAssets();
      setAvailableAssets(data.assets || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyAssets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await assetUserService.getMyAssets();
      setMyAssets(data.assets || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await assetUserService.getMyRequests();
      setMyRequests(data.requests || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user.role !== 'asset_user') {
      navigate('/');
      return;
    }
    fetchStats();
    fetchComplianceData();
  }, [navigate, user.role, fetchStats, fetchComplianceData]);

  useEffect(() => {
    if (activeTab === 'all-assets') {
      fetchAllAssets();
    } else if (activeTab === 'available') {
      fetchAvailable();
    } else if (activeTab === 'my-assets') {
      fetchMyAssets();
    } else if (activeTab === 'my-requests') {
      fetchMyRequests();
    }
  }, [activeTab, currentPage, filterCategory, filterStatus, filterCondition, fetchAllAssets, fetchAvailable, fetchMyAssets, fetchMyRequests]);

  const handleRaiseRequest = (asset) => {
    navigate('/asset-request', { state: { assetId: asset._id } });
  };

  // Edit request handlers
  const handleEditClick = async (request) => {
    setEditingRequest(request._id);
    setEditForm({
      department: request.department || '',
      deskLocation: request.deskLocation || '',
      reason: request.reason || '',
      duration: request.duration || '',
      startDate: request.startDate ? new Date(request.startDate).toISOString().split('T')[0] : ''
    });
    // Fetch departments for the dropdown
    try {
      const locData = await assetUserService.getLocations();
      setDepartments(locData.locations || []);
    } catch (err) {
      console.error('Failed to fetch departments', err);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSave = async (requestId) => {
    setEditLoading(true);
    try {
      await assetUserService.updateAssetRequest(requestId, editForm);
      setEditingRequest(null);
      setEditForm({});
      fetchMyRequests();
    } catch (err) {
      setError(err.message || 'Failed to update request');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditingRequest(null);
    setEditForm({});
  };

  const handleDeleteRequest = async (requestId) => {
    setLoading(true);
    try {
      await assetUserService.deleteAssetRequest(requestId);
      setDeleteConfirm(null);
      fetchMyRequests();
    } catch (err) {
      setError(err.message || 'Failed to delete request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: '#f59e0b', icon: <FaClock /> },
      approved: { color: '#10b981', icon: <FaCheckCircle /> },
      rejected: { color: '#ef4444', icon: <FaTimesCircle /> }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <div className="status-badge" style={{ color: config.color }}>
        {config.icon} {status.charAt(0).toUpperCase() + status.slice(1)}
      </div>
    );
  };

  const getStatusLabel = (s) => {
    const map = { available: 'Available', in_use: 'In Use', under_maintenance: 'Maintenance', disposed: 'Disposed' };
    return map[s] || s;
  };

  const getComplianceDueDate = (assetId) => {
    return complianceData[assetId] || null;
  };

  const isComplianceOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getComplianceStatusBadge = (dueDate) => {
    if (!dueDate) return null;
    const daysLeft = getDaysUntilDue(dueDate);
    
    if (daysLeft < 0) {
      return <span style={{ color: '#ef4444', fontWeight: '600', fontSize: '12px' }}>⚠️ OVERDUE</span>;
    } else if (daysLeft === 0) {
      return <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '12px' }}>⚡ DUE TODAY</span>;
    } else if (daysLeft <= 7) {
      return <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '12px' }}>⏰ DUE SOON ({daysLeft}d)</span>;
    } else if (daysLeft <= 30) {
      return <span style={{ color: '#8b5cf6', fontWeight: '600', fontSize: '12px' }}>📅 {daysLeft} days left</span>;
    }
    return null;
  };

  // Prepare chart data
  const statusPieData = stats ? [
    { name: 'Available', value: stats.statusBreakdown.available },
    { name: 'In Use', value: stats.statusBreakdown.inUse },
    { name: 'Maintenance', value: stats.statusBreakdown.underMaintenance },
    { name: 'Disposed', value: stats.statusBreakdown.disposed }
  ].filter(d => d.value > 0) : [];

  const assignmentPieData = stats?.assignmentBreakdown ? [
    { name: 'Unassigned', value: stats.assignmentBreakdown.unassigned },
    { name: 'Assigned', value: stats.assignmentBreakdown.assigned }
  ].filter(d => d.value > 0) : [];
  const ASSIGNMENT_COLORS = ['#6ee7b7', '#f87171'];

  const conditionPieData = stats ? [
    { name: 'Excellent', value: stats.conditionBreakdown.excellent },
    { name: 'Good', value: stats.conditionBreakdown.good },
    { name: 'Fair', value: stats.conditionBreakdown.fair },
    { name: 'Damaged', value: stats.conditionBreakdown.damaged }
  ].filter(d => d.value > 0) : [];

  const categoryBarData = stats?.byCategory || [];
  const locationBarData = stats?.byLocation || [];

  // Trend data for line chart (mock data based on stats)
  const assetTrendData = stats ? [
    { month: 'Jan', assigned: Math.floor(stats.assignmentBreakdown.assigned * 0.6), available: Math.floor(stats.assignmentBreakdown.unassigned * 0.7) },
    { month: 'Feb', assigned: Math.floor(stats.assignmentBreakdown.assigned * 0.7), available: Math.floor(stats.assignmentBreakdown.unassigned * 0.65) },
    { month: 'Mar', assigned: Math.floor(stats.assignmentBreakdown.assigned * 0.8), available: Math.floor(stats.assignmentBreakdown.unassigned * 0.6) },
    { month: 'Apr', assigned: Math.floor(stats.assignmentBreakdown.assigned * 0.9), available: Math.floor(stats.assignmentBreakdown.unassigned * 0.55) },
    { month: 'May', assigned: Math.floor(stats.assignmentBreakdown.assigned * 0.95), available: Math.floor(stats.assignmentBreakdown.unassigned * 0.5) },
    { month: 'Jun', assigned: stats.assignmentBreakdown.assigned, available: stats.assignmentBreakdown.unassigned }
  ] : [];

  // Request approval rate
  const totalRequests = stats?.myRequests ? (stats.myRequests.pending + stats.myRequests.approved + stats.myRequests.rejected) : 0;
  const approvalRate = totalRequests > 0 ? Math.round((stats.myRequests.approved / totalRequests) * 100) : 0;

  return (
    <div className="asset-user-page">
      <AssetuserSidebar user={user} activeTab={activeTab} onTabChange={setActiveTab} />
      <div className="page-container">
        <div className="asset-user-dashboard">
          {error && <div className="error-message">{error}</div>}

          {/* Tabs */}
        

      {/* ─── OVERVIEW TAB ─── */}
      {activeTab === 'overview' && (
        <div className="overview-section">
          {statsLoading ? <div className="loading">Loading statistics...</div> : stats && (
            <>
              {/* Stat Cards */}
              <div className="stat-cards-grid">
                <div className="stat-card card-blue">
                  <div className="stat-icon"><FaBox /></div>
                  <div className="stat-info">
                    <span className="stat-number">{stats.totalAssets}</span>
                    <span className="stat-label">Total Assets</span>
                  </div>
                </div>
                <div className="stat-card card-green">
                  <div className="stat-icon"><FaCheckCircle /></div>
                  <div className="stat-info">
                    <span className="stat-number">{stats.assignmentBreakdown?.unassigned ?? stats.statusBreakdown.available}</span>
                    <span className="stat-label">Unassigned</span>
                  </div>
                </div>
                <div className="stat-card card-indigo">
                  <div className="stat-icon"><FaTools /></div>
                  <div className="stat-info">
                    <span className="stat-number">{stats.assignmentBreakdown?.assigned ?? stats.statusBreakdown.inUse}</span>
                    <span className="stat-label">Assigned</span>
                  </div>
                </div>
                <div className="stat-card card-amber">
                  <div className="stat-icon"><FaWrench /></div>
                  <div className="stat-info">
                    <span className="stat-number">{stats.statusBreakdown.underMaintenance}</span>
                    <span className="stat-label">Under Maintenance</span>
                  </div>
                </div>
              
                  
                <div className="stat-card card-purple">
                  <div className="stat-icon"><FaClipboardList /></div>
                  <div className="stat-info">
                    <span className="stat-number">{stats.myAssets}</span>
                    <span className="stat-label">My Assets</span>
                  </div>
                </div>
              </div>

              {/* My Requests Summary */}
              <div className="my-requests-summary">
                <h3>My Requests</h3>
                <div className="request-summary-row">
                  <div className="req-stat pending"><FaClock /> <span>{stats.myRequests.pending} Pending</span></div>
                  <div className="req-stat approved"><FaCheckCircle /> <span>{stats.myRequests.approved} Approved</span></div>
                  <div className="req-stat rejected"><FaTimesCircle /> <span>{stats.myRequests.rejected} Rejected</span></div>
                  <div className="req-stat-rate"><FaChartLine /> <span>Approval Rate: {approvalRate}%</span></div>
                </div>
              </div>

              {/* Key Metrics Cards */}
              <div className="metrics-cards-row">
                <div className="metrics-card metrics-card-1">
                  <div className="metrics-card-icon"><FaChartPie /></div>
                  <div className="metrics-card-content">
                    <span className="metrics-label">Total Categories</span>
                    <span className="metrics-value">{categoryBarData.length}</span>
                  </div>
                </div>
                <div className="metrics-card metrics-card-2">
                  <div className="metrics-card-icon"><FaExclamationTriangle /></div>
                  <div className="metrics-card-content">
                    <span className="metrics-label">Assets Needing Attention</span>
                    <span className="metrics-value">{stats.statusBreakdown.underMaintenance + stats.statusBreakdown.disposed}</span>
                  </div>
                </div>
                <div className="metrics-card metrics-card-3">
                  <div className="metrics-card-icon"><FaChartLine/></div>
                  <div className="metrics-card-content">
                    <span className="metrics-label">Utilization Rate</span>
                    <span className="metrics-value">
                      {stats.totalAssets > 0 ? Math.round((stats.assignmentBreakdown.assigned / stats.totalAssets) * 100) : 0}%
                    </span>
                  </div>
                </div>
                <div className="metrics-card metrics-card-4">
                  <div className="metrics-card-icon"><FaFileAlt /></div>
                  <div className="metrics-card-content">
                    <span className="metrics-label">Total Requests</span>
                    <span className="metrics-value">{totalRequests}</span>
                  </div>
                </div>
              </div>

              {/* Charts Row */}
              <div className="charts-grid">
                {/* Status Pie */}
                <div className="chart-card">
                  <h3><FaChartPie style={{ marginRight: 8 }} />Assets by Status</h3>
                  {statusPieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                          {statusPieData.map((_, i) => <Cell key={i} fill={Object.values(STATUS_COLORS)[i]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="no-data">No data</p>}
                </div>

                {/* Assignment Pie */}
                <div className="chart-card">
                  <h3><FaChartPie style={{ marginRight: 8 }} />Assignment Status</h3>
                  {assignmentPieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={assignmentPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                          {assignmentPieData.map((_, i) => <Cell key={i} fill={ASSIGNMENT_COLORS[i]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="no-data">No data</p>}
                </div>

                {/* Condition Pie */}
                <div className="chart-card">
                  <h3><FaChartPie style={{ marginRight: 8 }} />Assets by Condition</h3>
                  {conditionPieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={conditionPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                          {conditionPieData.map((_, i) => <Cell key={i} fill={Object.values(CONDITION_COLORS)[i]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="no-data">No data</p>}
                </div>

                {/* Asset Assignment Trend - Line Chart */}
                <div className="chart-card chart-wide">
                  <h3><FaChartLine style={{ marginRight: 8 }} />6-Month Assignment Trend</h3>
                  {assetTrendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={assetTrendData}>
                        <CartesianGrid strokeDasharray="5 5" stroke="#e5e7eb" />
                        <XAxis dataKey="month" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="assigned" 
                          stroke="#60a5fa" 
                          strokeWidth={3}
                          dot={{ fill: '#60a5fa', r: 5 }}
                          activeDot={{ r: 7 }}
                          name="Assigned Assets"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="available" 
                          stroke="#6ee7b7" 
                          strokeWidth={3}
                          dot={{ fill: '#6ee7b7', r: 5 }}
                          activeDot={{ r: 7 }}
                          name="Available Assets"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <p className="no-data">No trend data</p>}
                </div>

                {/* Category Bar - Slim Bar Chart */}
                <div className="chart-card chart-wide">
                  <h3><FaChartBar style={{ marginRight: 8 }} />Assets by Category</h3>
                  {categoryBarData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={categoryBarData}>
                        <CartesianGrid strokeDasharray="5 5" stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} angle={-20} textAnchor="end" height={60} stroke="#9ca3af" />
                        <YAxis allowDecimals={false} stroke="#9ca3af" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        />
                        <Bar dataKey="count" fill="#a5d6ff" radius={[6, 6, 0, 0]} barSize={40}>
                          {categoryBarData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="no-data">No category data</p>}
                </div>

                {/* Location Bar - Slim Bar Chart */}
                <div className="chart-card chart-wide">
                  <h3><FaChartBar style={{ marginRight: 8 }} />Assets by Department</h3>
                  {locationBarData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={locationBarData}>
                        <CartesianGrid strokeDasharray="5 5" stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} angle={-20} textAnchor="end" height={60} stroke="#9ca3af" />
                        <YAxis allowDecimals={false} stroke="#9ca3af" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        />
                        <Bar dataKey="count" name="Assets" fill="#b3f5e8" radius={[6, 6, 0, 0]} barSize={40} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="no-data">No location data</p>}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─── ALL ASSETS TAB ─── */}
      {activeTab === 'all-assets' && (
        <div className="tab-content">
          <div className="all-assets-header">
            <h2>All Assets ({totalAssetCount})</h2>
            <div className="filters-bar">
             
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
                <option value="">All Status</option>
                <option value="available">Available</option>
                <option value="in_use">In Use</option>
                <option value="under_maintenance">Under Maintenance</option>
                <option value="disposed">Disposed</option>
              </select>
              <select value={filterCondition} onChange={(e) => { setFilterCondition(e.target.value); setCurrentPage(1); }}>
                <option value="">All Conditions</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="damaged">Damaged</option>
              </select>
              {stats?.byCategory && (
                <select value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); setCurrentPage(1); }}>
                  <option value="">All Categories</option>
                  {stats.byCategory.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              )}
            </div>
          </div>

          {loading ? <div className="loading">Loading assets...</div> : allAssets.length === 0 ? (
            <p className="empty-message">No assets match your filters.</p>
          ) : (
            <>
              <div className="assets-table-wrapper">
                <table className="assets-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Serial #</th>
                      <th>Category</th>
                      <th>Brand / Model</th>
                      <th>Department</th>
                      <th>Status</th>
                      <th>Assigned</th>
                      <th>Condition</th>
                      <th>Compliance Due</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allAssets.map((asset) => (
                      <tr key={asset._id}>
                        <td className="asset-name-cell">{asset.assetName}</td>
                        <td>{asset.serialNumber || '—'}</td>
                        <td><span className="category-badge-sm">{asset.category}</span></td>
                        <td>{[asset.brand, asset.model].filter(Boolean).join(' / ') || '—'}</td>
                        <td>{asset.department}</td>
                        <td><span className={`status-pill status-${asset.status}`}>{getStatusLabel(asset.status)}</span></td>
                        <td><span className={`status-pill ${asset.isAssigned ? 'status-assigned' : 'status-unassigned'}`}>{asset.isAssigned ? 'Taken' : 'Free'}</span></td>
                        <td><span className={`condition ${asset.condition}`}>{asset.condition}</span></td>
                        <td>
                          {(() => {
                            const dueDate = getComplianceDueDate(asset._id);
                            if (!dueDate) return <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>;
                            const isOverdue = isComplianceOverdue(dueDate);
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
                                <span style={{ fontSize: '12px', color: isOverdue ? '#ef4444' : '#2d3748', fontWeight: isOverdue ? '600' : 'normal' }}>
                                  {new Date(dueDate).toLocaleDateString()}
                                </span>
                                {getComplianceStatusBadge(dueDate)}
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          {!asset.isAssigned && asset.status !== 'disposed' ? (
                            <button className="btn-request-sm" onClick={() => handleRaiseRequest(asset)}>
                              <FaPlus /> Request
                            </button>
                          ) : <span className="text-muted">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="pagination">
                  <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                  <span>Page {currentPage} of {totalPages}</span>
                  <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── AVAILABLE ASSETS TAB ─── */}
      {activeTab === 'available' && (
        <div className="tab-content">
          <h2>Available Assets</h2>
          {loading ? <div className="loading">Loading...</div> : availableAssets.length === 0 ? (
            <p className="empty-message">No assets available right now.</p>
          ) : (
            <div className="assets-grid">
              {availableAssets.map((asset) => (
                <div key={asset._id} className="asset-card">
                  <div className="card-header">
                    <h3>{asset.assetName}</h3>
                    <span className="category-badge">{asset.category}</span>
                  </div>
                  <div className="card-info">
                    <div><strong>Serial:</strong> {asset.serialNumber || '—'}</div>
                    <div><strong>Brand:</strong> {asset.brand || '—'}</div>
                    <div><strong>Model:</strong> {asset.model || '—'}</div>
                    <div><strong>Department:</strong> {asset.department}</div>
                    <div><strong>Status:</strong> <span className={`status-pill status-${asset.status}`}>{getStatusLabel(asset.status)}</span></div>
                    <div><strong>Condition:</strong> <span className={`condition ${asset.condition}`}>{asset.condition}</span></div>
                    {(() => {
                      const dueDate = getComplianceDueDate(asset._id);
                      if (dueDate) {
                        const isOverdue = isComplianceOverdue(dueDate);
                        return (
                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                            <strong style={{ fontSize: '12px', color: isOverdue ? '#ef4444' : '#8b5cf6' }}>
                              Compliance Due: {new Date(dueDate).toLocaleDateString()}
                            </strong>
                            <div style={{ marginTop: '2px' }}>
                              {getComplianceStatusBadge(dueDate)}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <button className="btn-request" onClick={() => handleRaiseRequest(asset)}>
                    <FaPlus /> Request Asset
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── MY ASSETS TAB ─── */}
      {activeTab === 'my-assets' && (
        <div className="tab-content">
          <h2>Assets Assigned to You</h2>
          {loading ? <div className="loading">Loading...</div> : myAssets.length === 0 ? (
            <p className="empty-message">You don't have any assets assigned yet.</p>
          ) : (
            <div className="assets-grid">
              {myAssets.map((assignment) => (
                <div key={assignment._id} className="asset-card assigned">
                  <div className="card-header">
                    <h3>{assignment.assetId?.assetName}</h3>
                    <span className="assigned-badge">Assigned</span>
                  </div>
                  <div className="card-info">
                    <div><strong>Serial:</strong> {assignment.assetId?.serialNumber || '—'}</div>
                    <div><strong>Category:</strong> {assignment.assetId?.category}</div>
                    <div><strong>Department:</strong> {assignment.assetId?.department}</div>
                    <div><strong>Desk:</strong> {assignment.deskLocation || '—'}</div>
                    <div><strong>Assigned By:</strong> {assignment.assignedBy?.name}</div>
                    <div><strong>Assigned Date:</strong> {new Date(assignment.assignedDate).toLocaleDateString()}</div>
                    {(() => {
                      const dueDate = getComplianceDueDate(assignment.assetId?._id);
                      if (dueDate) {
                        const isOverdue = isComplianceOverdue(dueDate);
                        return (
                          <div style={{ 
                            marginTop: '10px', 
                            padding: '8px 10px', 
                            backgroundColor: isOverdue ? '#fee2e2' : '#fef3c7',
                            borderRadius: '6px',
                            borderLeft: `3px solid ${isOverdue ? '#ef4444' : '#f59e0b'}`
                          }}>
                            <strong style={{ color: isOverdue ? '#991b1b' : '#92400e' }}>Compliance Due:</strong>
                            <div style={{ marginTop: '4px', fontSize: '13px', color: isOverdue ? '#991b1b' : '#92400e', fontWeight: '500' }}>
                              {new Date(dueDate).toLocaleDateString()}
                            </div>
                            {getComplianceStatusBadge(dueDate) && (
                              <div style={{ marginTop: '4px' }}>
                                {getComplianceStatusBadge(dueDate)}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  {assignment.remarks && (
                    <div className="remarks">
                      <strong>Notes:</strong> {assignment.remarks}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── MY REQUESTS TAB ─── */}
      {activeTab === 'my-requests' && (
        <div className="tab-content">
          <h2>Your Asset Requests</h2>

          {/* Status Filter Bar */}
          <div className="request-filter-bar">
            {['all', 'pending', 'approved', 'rejected'].map((f) => (
              <button
                key={f}
                className={`request-filter-btn ${requestStatusFilter === f ? 'active' : ''} ${f}`}
                onClick={() => setRequestStatusFilter(f)}
              >
                {f === 'all' && <FaClipboardList />}
                {f === 'pending' && <FaClock />}
                {f === 'approved' && <FaCheckCircle />}
                {f === 'rejected' && <FaTimesCircle />}
                {' '}{f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {loading ? <div className="loading">Loading...</div> : myRequests.length === 0 ? (
            <p className="empty-message">You haven't made any asset requests yet.</p>
          ) : (() => {
            const statusOrder = { pending: 0, approved: 1, rejected: 2 };
            const filtered = myRequests
              .filter(r => requestStatusFilter === 'all' || r.status === requestStatusFilter)
              .sort((a, b) => (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3) || new Date(b.createdAt) - new Date(a.createdAt));
            return filtered.length === 0 ? (
              <p className="empty-message">No {requestStatusFilter} requests found.</p>
            ) : (
            <div className="requests-list">
              {filtered.map((request) => (
                <div key={request._id} className="request-card">
                  <div className="request-header">
                    <h3>Asset Name: {request.assetId?.assetName}</h3>
                    <div className="request-header-right">
                      {getStatusBadge(request.status)}
                      {request.status === 'pending' && (
                        <div className="request-actions-btns">
                          <button
                            className="btn-req-edit"
                            title="Edit Request"
                            onClick={() => handleEditClick(request)}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn-req-delete"
                            title="Delete Request"
                            onClick={() => setDeleteConfirm(request._id)}
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Inline Edit Form */}
                  {editingRequest === request._id ? (
                    <div className="request-edit-form">
                      <div className="edit-form-grid">
                        <div className="edit-form-group">
                          <label>Department *</label>
                          <select name="department" value={editForm.department} onChange={handleEditChange}>
                            <option value="">-- Select --</option>
                            {departments.map(d => (
                              <option key={d._id} value={d.label || d.name}>{d.label || d.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="edit-form-group">
                          <label>Duration *</label>
                          <select name="duration" value={editForm.duration} onChange={handleEditChange}>
                            <option value="">-- Select --</option>
                            {DURATION_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="edit-form-group">
                          <label>From Date *</label>
                          <input
                            type="date"
                            name="startDate"
                            value={editForm.startDate}
                            onChange={handleEditChange}
                            min={new Date().toISOString().split('T')[0]}
                          />
                        </div>
                        <div className="edit-form-group">
                          <label>Desk / Station</label>
                          <input
                            type="text"
                            name="deskLocation"
                            value={editForm.deskLocation}
                            onChange={handleEditChange}
                            placeholder="e.g., Desk A-5"
                          />
                        </div>
                      </div>
                      <div className="edit-form-group full-width">
                        <label>Reason</label>
                        <textarea
                          name="reason"
                          value={editForm.reason}
                          onChange={handleEditChange}
                          rows="3"
                          placeholder="Why do you need this asset?"
                        />
                      </div>
                      <div className="edit-form-actions">
                        <button className="btn-edit-save" onClick={() => handleEditSave(request._id)} disabled={editLoading}>
                          <FaSave /> {editLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button className="btn-edit-cancel" onClick={handleEditCancel}>
                          <FaTimes /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="request-info">
                      <div><strong>Asset Serial:</strong> {request.assetId?.serialNumber}</div>
                      <div><strong>Request Type:</strong> {request.requestType === 'swap_request' ? 'Swap Request' : 'New Request'}</div>
                      <div><strong>Department:</strong> {request.department}</div>
                      {request.startDate && <div><strong>From Date:</strong> {new Date(request.startDate).toLocaleDateString()}</div>}
                      {request.duration && <div><strong>Duration:</strong> {request.duration.replace(/_/g, ' ')}</div>}
                      {request.deskLocation && <div><strong>Desk / Station:</strong> {request.deskLocation}</div>}
                      {request.reason && <div><strong>Reason:</strong> {request.reason}</div>}
                      <div><strong>Requested On:</strong> {new Date(request.createdAt).toLocaleDateString()}</div>
                    </div>
                  )}

                  {request.status === 'approved' && (
                    <div className="approval-info">
                      <strong>Approved By:</strong> {request.approvedBy?.name}
                    </div>
                  )}
                  {request.status === 'rejected' && (
                    <div className="rejection-info">
                      <strong>Rejection Reason:</strong> {request.rejectionReason || 'Not specified'}
                    </div>
                  )}

                  {/* Delete Confirmation */}
                  {deleteConfirm === request._id && (
                    <div className="delete-confirm-bar">
                      <span>Are you sure you want to delete this request?</span>
                      <div className="delete-confirm-actions">
                        <button className="btn-confirm-yes" onClick={() => handleDeleteRequest(request._id)}>
                          Yes, Delete
                        </button>
                        <button className="btn-confirm-no" onClick={() => setDeleteConfirm(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            );
          })()}
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default AssetUserDashboard;
